import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAide } from './AideContexte';
import { aideAPI } from '../../services/api';
import './Aide.css';

/**
 * « PROPOSER UNE AMÉLIORATION » — commun à tous les modules (étape A3 ; « Signaler un manque dans cette aide » avant v99)
 * ============================================================================
 * Protocole : doc/specs_techniques/aide_contextuelle.md § 6 et 6 ter · Maquette : fenêtre « Proposer une amélioration »
 *
 * v99 (2026-10-29, Bertrand) : on propose une amélioration du MODULE, pas seulement de l'aide. Trois pastilles disent la
 * nature (aide · idée, cochée par défaut · défaut) ; « Aucune tâche ne correspond » (Je veux…) ouvre sur « aide ».
 *
 * Le module et l'écran sont remplis d'office (l'écran ouvert à l'instant du clic) ; le texte (1 000 caractères au
 * plus) dit ce qui a manqué. Le serveur ENREGISTRE toujours, puis prévient les super-administrateurs par courriel ;
 * l'écran dit merci sans parler de la boîte d'envoi : la panne éventuelle se voit dans Administration → Aide.
 */
const MAX = 1000;
const NATURES = [
    ['aide', '📘', "L'aide ne répond pas à ma question"],
    ['idee', '💡', 'Une idée pour améliorer le module'],
    ['defaut', '⚠️', 'Quelque chose ne marche pas']
];

function SignalerManque() {
    const aide = useAide();
    const ouvert = !!(aide && aide.signalerOuvert);
    const [texte, setTexte] = useState('');
    const [etat, setEtat] = useState('saisie');           // saisie | envoi | merci
    const [erreur, setErreur] = useState(null);
    const [ecranFige, setEcranFige] = useState('');
    const [nature, setNature] = useState('idee');

    // À chaque ouverture : un formulaire vierge, l'écran du moment.
    useEffect(() => {
        if (!ouvert) return;
        setTexte(''); setEtat('saisie'); setErreur(null); setEcranFige(aide.ecran);
        setNature(aide.signalerOuvert === 'aide' ? 'aide' : 'idee');
    }, [ouvert]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!ouvert) return null;
    const { donnees } = aide;
    const libelleEcran = (() => {
        const g = ecranFige.split('/')[0];
        const e = donnees.ECRANS.find((x) => x.cle === ecranFige);
        const grp = donnees.GROUPES[g] || g;
        return e && e.libelle !== grp ? `${grp} › ${e.libelle}` : grp;
    })();
    const fermer = () => aide.setSignalerOuvert(false);
    const envoyer = async () => {
        setEtat('envoi'); setErreur(null);
        try {
            await aideAPI.signaler(aide.departementId, { module: donnees.module, ecran: ecranFige, nature, texte });
            setEtat('merci');
        } catch (e) {
            setErreur(e.response?.data?.error || "La proposition n'a pas pu être envoyée");
            setEtat('saisie');
        }
    };

    return createPortal(
        <div className="aide-ov aide-ov-haut" onMouseDown={(ev) => { if (ev.target === ev.currentTarget && etat !== 'envoi') fermer(); }}>
            <div className="aide-fen aide-sg" role="dialog" aria-modal="true" aria-label="Proposer une amélioration">
                <div className="aide-fen-h">
                    <i className="fa-solid fa-lightbulb" style={{ color: '#b45309' }} />
                    <span className="aide-fen-tt">Proposer une amélioration — {donnees.titreModule}</span>
                    <button type="button" className="aide-v-x" onClick={fermer} title="Fermer (Échap)" aria-label="Fermer"><i className="fa-solid fa-xmark" /></button>
                </div>
                {etat === 'merci' ? (
                    <>
                        <div className="aide-fen-b aide-sg-merci">
                            <i className="fa-solid fa-circle-check" />
                            <b>Merci, votre proposition est transmise.</b>
                            <span>Elle sera étudiée pour faire évoluer l'application, et l'administrateur pourra vous répondre par courriel.</span>
                        </div>
                        <div className="aide-fen-f"><button type="button" className="aide-btn-aller" onClick={fermer}>Fermer</button></div>
                    </>
                ) : (
                    <>
                        <div className="aide-fen-b">
                            <p className="aide-sg-intro">Une idée pour améliorer ce module, une difficulté rencontrée, une explication qui manque dans l'aide : dites-le, c'est lu.</p>
                            <div className="aide-sg-nat" role="radiogroup" aria-label="Nature de la proposition">
                                {NATURES.map(([v, ic, l]) => (
                                    <button key={v} type="button" role="radio" aria-checked={nature === v}
                                            className={'aide-sg-nat-p' + (nature === v ? ' on' : '')} onClick={() => setNature(v)}>{ic} {l}</button>
                                ))}
                            </div>
                            <div className="aide-sg-ligne"><b>Écran</b> {libelleEcran}</div>
                            <textarea className="aide-sg-texte" maxLength={MAX} value={texte} autoFocus
                                      onChange={(e) => setTexte(e.target.value)} aria-label="Votre proposition"
                                      placeholder="Ce que vous voulez faire et ce qui vous en empêche, ou ce qui pourrait être mieux. Ex. : j'aimerais que les dossiers à corriger remontent en haut du tableau." />
                            <div className="aide-sg-cpt">{texte.length} / {MAX}</div>
                            <div className="aide-sg-av"><i className="fa-solid fa-user-shield" /> N'écrivez pas de nom d'étudiant : décrivez la situation.</div>
                            <div className="aide-sg-info">Votre proposition est envoyée par courriel à l'administrateur de l'application ; elle est aussi conservée, pour qu'aucune ne se perde.</div>
                            {erreur && <div className="aide-sg-erreur"><i className="fa-solid fa-circle-exclamation" /> {erreur}</div>}
                        </div>
                        <div className="aide-fen-f">
                            <button type="button" className="aide-btn-sec" onClick={fermer} disabled={etat === 'envoi'}>Annuler</button>
                            <button type="button" className="aide-btn-aller" onClick={envoyer} disabled={!texte.trim() || etat === 'envoi'}>
                                <i className={'fa-solid ' + (etat === 'envoi' ? 'fa-circle-notch fa-spin' : 'fa-paper-plane')} /> Envoyer
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>,
        document.body
    );
}

export default SignalerManque;
